import { useEffect, useMemo, useRef, useState } from "react";
import { clsx } from "clsx";
import { IconPlus, IconX } from "@tabler/icons-react";
import type { ProductDto } from "../../../../api";
import { Image } from "../../../../components/common/image";
import { Button } from "../../../../components/common/button";
import { Text } from "../../../../components/common/typography";
import { useCompareUiStore } from "../../../../hooks/stores/useCompareUiStore";
import { AddToComparisonDialog } from "./add-to-comparison-dialog";

/** What the tray shows per product. `id` is the product template id. */
export type CompareTrayItem = Pick<ProductDto, "id" | "name" | "image" | "description">;

interface ProductsCompareBannerProps {
  selectedProducts: CompareTrayItem[];
  onRemove: (productId: number) => void;
  /**
   * Adds a product picked from the catalog search. "Add another product" is
   * only offered when this is provided.
   */
  onAddProduct?: (product: ProductDto) => void;
  /**
   * Template ids the caller compares on top of `selectedProducts` (e.g. the
   * product-details page's own product), kept out of the catalog search.
   */
  pinnedProductIds?: number[];
  onCompare: () => void;
  onClear: () => void;
  maxCompare?: number;
  /**
   * Minimum number of selected products required to enable "Compare now".
   * Lower this when the caller contributes extra products to the comparison
   * (e.g. the product-details page pins the currently-viewed product).
   */
  minCompare?: number;
}

export function ProductsCompareBanner({
  selectedProducts,
  onRemove,
  onAddProduct,
  pinnedProductIds,
  onCompare,
  onClear,
  maxCompare = 3,
  minCompare = 2,
}: ProductsCompareBannerProps) {
  const setCompareAnchor = useCompareUiStore((s) => s.setCompareAnchor);
  const bannerRef = useRef<HTMLDivElement>(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const isVisible = selectedProducts.length > 0;
  const comparedProductIds = useMemo(
    () => new Set([...selectedProducts.map((p) => p.id), ...(pinnedProductIds ?? [])]),
    [selectedProducts, pinnedProductIds],
  );

  useEffect(() => {
    setCompareAnchor(bannerRef.current);
    return () => setCompareAnchor(null);
  }, [setCompareAnchor]);

  return (
    <div
      ref={bannerRef}
      aria-hidden={!isVisible}
      className={clsx(
        "fixed bottom-0 left-0 right-0 z-50 border-t border-borderDefault bg-bgWhite px-8 py-3 max-md:px-4 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] transition-opacity duration-200",
        isVisible ? "opacity-100" : "pointer-events-none opacity-0",
      )}
    >
      <div className="flex items-center gap-4 max-md:flex-wrap max-md:gap-2">
        <Text variant="bodySmBold" tone="dark" className="shrink-0">
          Comparing:
        </Text>

        <div className="flex flex-1 items-center gap-3 max-md:order-last max-md:basis-full max-md:overflow-x-auto">
          {selectedProducts.map((product) => (
            <CompareTrayChip
              key={product.id}
              product={product}
              onRemove={() => onRemove(product.id)}
            />
          ))}

          {onAddProduct && selectedProducts.length < maxCompare && (
            <button
              type="button"
              onClick={() => setIsPickerOpen(true)}
              className="flex items-center gap-2 rounded-lg border border-dashed border-borderDark px-4 py-2 text-textMuted transition-colors hover:border-brandPrimary hover:text-textDark"
            >
              <IconPlus size={14} />
              <Text variant="bodySm" tone="inherit" inline>
                Add another product
              </Text>
            </button>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-3 max-md:ml-auto">
          <Text variant="bodySm" tone="muted">
            {selectedProducts.length} of {maxCompare} selected
          </Text>
          <Button
            variant="primary"
            size="sm"
            onClick={onCompare}
            disabled={selectedProducts.length < minCompare}
          >
            Compare now &rsaquo;
          </Button>
          <Button variant="secondary" size="sm" onClick={onClear}>
            Clear all
          </Button>
        </div>
      </div>

      {onAddProduct && (
        <AddToComparisonDialog
          open={isPickerOpen}
          onOpenChange={setIsPickerOpen}
          onSelect={onAddProduct}
          excludeProductIds={comparedProductIds}
        />
      )}
    </div>
  );
}

function CompareTrayChip({
  product,
  onRemove,
}: {
  product: CompareTrayItem;
  onRemove: () => void;
}) {
  return (
    <div className="flex w-56 shrink-0 items-center gap-2 rounded-lg border border-borderDefault bg-bgLight px-3 py-2">
      <div className="size-9 shrink-0 overflow-hidden rounded">
        <Image
          image={product.image}
          alt={product.name ?? ""}
          size="w-full h-full"
          showPlaceholder={false}
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <Text
          variant="bodySmBold"
          tone="dark"
          inline
          className="block truncate"
        >
          {product.name ?? "—"}
        </Text>
        {product.description ? (
          <Text variant="bodyXs" tone="muted" inline className="block truncate">
            {product.description}
          </Text>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="ml-1 flex size-5 shrink-0 items-center justify-center rounded-full text-textMuted hover:text-textDark"
        aria-label="Remove from comparison"
      >
        <IconX size={14} />
      </button>
    </div>
  );
}
